import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Table from '@src/components/NewTable';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { Typography } from '@mui/material';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import {
  HEIGHT_CONTENT,
  MAX_BODY_CONTENT_HEIGHT,
} from '@src/constants/request';
import { extractVoiceName } from '@src/services/voice';
import RequestStatus from '@src/components/RequestStatus';
import { capitalize } from 'lodash';
import { PACKAGE_TYPE } from '@src/constants/package';
import ProjectVoice from '@src/containers/NewDubbing/ListProject/ProjectTableItem/ProjectVoice';
import { VOICE_LEVEL, VOICE_OWNERSHIP } from '@src/constants/voice';
import { LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import ViewRequest from './ViewRequest';
import RequestAction from './RequestAction';

import { StyledRequestTable, StyleVoiceItem } from './index.style';
import TitleTable from './TitleTable';
import RequestsActionHeader from './RequestsActionHeader';

const RequestTable = ({
  isExpand,
  rowsPerPage,
  flickerBackground,
  requests = [],
  page,
  requestListHeight,
  total,
  sort,
  loading,
  onChangeRowPage,
  selectedRequests,
  setNumSelected,
  onUpdateRequest,
  onChangePage,
  onChangeSort,
  onSelectRequest,
  onDeleteSelectedRequests,
  onEnableEditor,
  audioRef,
  playable = true,
  tableHeight,
  isShowCheckbox,
  limit,
  // isNewHomepage,
}) => {
  const requestTableRef = useRef(null);
  const [openView, setOpenView] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const screenHeight = window.innerHeight;
  const user = useSelector((state) => state.auth.user);

  const { filterVoice } = useSelector((state) => state.voice);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { languages } = useSelector((state) => state.language);

  const handleOpenView = (request) => {
    setOpenView(true);
    setSelectedRequest(request);
  };

  const handleCloseView = () => {
    setOpenView(false);
    setSelectedRequest(null);
  };

  const handleOpenErrorReport = (request) => {
    const ticketMetadata = {
      requestId: request.id,
      voiceCode: request.voice.code,
      userId: user.id,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    const path = window.location.pathname;
    const product = path.split('/')[2];
    const category =
      product === 'dubbing' ? CATEGORY_TYPE.DUBBING : CATEGORY_TYPE.TTS;

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${category}`,
      '_blank',
    );
    setSelectedRequest(request);
  };

  const renderVoiceName = (voice) => {
    const { voiceName, voiceType } = extractVoiceName({
      name: voice?.name,
      provider: voice?.provider,
    });
    const smallImgSrc =
      voice?.language?.roundImage ||
      languages.find((lang) => lang?.value === voice?.languageCode)?.roundImage;

    const voiceStyles = [
      voiceType || t(voice?.styles?.[0]),
      voice?.level !== VOICE_LEVEL.BASIC && t(voice?.level),
      voice?.beta && capitalize(t('beta')),
    ]
      ?.filter(Boolean)
      .join(' • ');

    return (
      <StyleVoiceItem>
        <BadgeAvatar
          width={30}
          img={voice?.roundImage}
          type="image"
          smallImgWidth={10}
          smallImg={smallImgSrc}
        />
        <div>
          <Typography variant="body2" style={{ whiteSpace: 'nowrap' }}>
            {voiceName}
          </Typography>
          <div className="new-voice-style">{voiceStyles}</div>
        </div>
      </StyleVoiceItem>
    );
  };

  const columns = [
    {
      field: 'title',
      title: t('title'),
      sortable: false,
      align: 'left',
      width: 'full',
      cellWidth: 'full',
      render: (row) => (
        <TitleTable
          requestId={row.id}
          title={row.title}
          audioLink={row.audioLink}
          audioType={row?.audioType}
          characters={row?.characters}
          requestStatus={row.status}
          onUpdateRequest={onUpdateRequest}
          createdAt={row.createdAt}
          credits={row?.credits}
        />
      ),
    },
    {
      field: 'voice',
      sortable: true,
      align: 'left',
      width: '20%',
      cellWidth: '20%',
      render: (row) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          {row?.sentencesVoiceCode && row?.type === PACKAGE_TYPE.DUBBING ? (
            <ProjectVoice
              voiceCode={row.voice.code}
              sentencesVoiceCode={JSON.parse(row?.sentencesVoiceCode || {})}
            />
          ) : (
            renderVoiceName(row.voice)
          )}
        </div>
      ),
    },
    {
      id: TTS_GUIDE.STATUS_REQUEST,
      field: 'status',
      width: '35%',
      render: (row) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <>
            <RequestStatus request={row} />
            <RequestAction
              audioRef={audioRef}
              request={row}
              playable={playable}
              onOpenView={handleOpenView}
              onOpenErrorReport={handleOpenErrorReport}
              isDisabledErrorReport={row?.isDisabledErrorReport}
            />
          </>
        </div>
      ),
    },
  ];

  const handleCloseBanner = () => {
    dispatch(actions.synthesisRequest.updateShowBannerProjects(false));
  };

  const handleRowClick = async (requestId) => {
    handleCloseBanner();
    if (document.getElementById('scroll-point-top'))
      document.getElementById('scroll-point-top').scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    dispatch(actions.synthesisRequest.updateLoadingLoadRequest(true));
    const data = await apis.requests.getRequestV2(requestId);

    dispatch(actions.synthesisRequest.updateLoadingLoadRequest(false));
    if (data?.status) {
      const synthesisConfig = data.result;
      const bitrateOfBps = synthesisConfig.bitrate || 0;
      const newSynthesisConfig = {
        ...synthesisConfig,
        bitrate: bitrateOfBps % 1000 === 0 ? bitrateOfBps / 1000 : bitrateOfBps,
      };

      const { voice } = synthesisConfig;
      if (voice?.hasEmphasis) {
        newSynthesisConfig.hasEmphasis = voice?.hasEmphasis;
        newSynthesisConfig.emphasis = synthesisConfig?.emphasisIntensity;
      }

      onEnableEditor(false);

      const hasSentences = data?.result?.sentences?.length > 0;
      const updateData = {
        title: newSynthesisConfig?.title || '',
        audioType: newSynthesisConfig.audioType,
        clientPause: newSynthesisConfig.clientPause || {},
        bitrate: newSynthesisConfig.bitrate,
        pronunciationDict: newSynthesisConfig.pronunciationDict || [],
        openSentences: hasSentences,
      };
      if (newSynthesisConfig.backgroundMusic?.link) {
        const { link, name, volume } = newSynthesisConfig.backgroundMusic ?? {};
        updateData.backgroundMusic = { link, name, volume };
      }
      if (!hasSentences) {
        updateData.voice = newSynthesisConfig.voice?.name;
        updateData.text = newSynthesisConfig.text || '';
        updateData.speed = newSynthesisConfig.speed;
      }

      if (hasSentences && data?.result?.sentences) {
        updateData.sentences = data.result.sentences.map((sentence) => ({
          breakTime: sentence?.breakTime,
          characters: sentence?.text?.length,
          speed: sentence?.speed,
          text: sentence?.text,
          voice_code: sentence.voiceCode,
        }));
      }

      dispatch(actions.synthesisRequest.updateDataRelatedRequest(updateData));
      dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig(newSynthesisConfig),
      );
      dispatch(
        actions.request.addSynthesisResponse({
          requestId: data.result.id,
          ...newSynthesisConfig,
        }),
      );
      dispatch(actions.synthesisRequest.updateLoadingSynthesisConfig(true));

      const isVoiceCloning = isClonedVoiceCode(synthesisConfig?.voice?.code);
      dispatch(
        actions.voice.changeFilterVoice({
          ...filterVoice,
          voiceOwnership: isVoiceCloning
            ? VOICE_OWNERSHIP.PERSONAL
            : VOICE_OWNERSHIP.VBEE,
        }),
      );
    }
  };

  const renderExtraHeader = (colPosition) => (
    <RequestsActionHeader
      selectedRequests={selectedRequests}
      onDeleteSelectedRequests={onDeleteSelectedRequests}
      colPosition={colPosition}
    />
  );

  return (
    <StyledRequestTable ref={requestTableRef}>
      <Table
        // isNewHomepage={isNewHomepage}
        isExpand={isExpand}
        columns={columns}
        flickerBackground={flickerBackground}
        data={requests}
        page={page}
        requestListHeight={requestListHeight}
        total={total}
        onChangeRowPage={onChangeRowPage}
        sort={sort}
        setNumSelected={setNumSelected}
        loading={loading}
        dropTable={!isExpand}
        selection
        onChangeSort={onChangeSort}
        rowsPerPage={rowsPerPage}
        onChangePage={onChangePage}
        onRowClick={handleRowClick}
        selected={selectedRequests}
        onSelect={onSelectRequest}
        minWidth="1000px"
        footerPadding="0px 20px 0px"
        showExtraHeaderComponent={selectedRequests?.length > 0}
        renderExtraHeader={renderExtraHeader}
        tableHeight={
          isExpand &&
          (tableHeight ||
            `${Math.min(
              screenHeight / 2 - HEIGHT_CONTENT,
              MAX_BODY_CONTENT_HEIGHT,
            )}px`)
        }
        isShowCheckbox={isShowCheckbox}
        headerStyle={{ zIndex: 10 }}
        isStickyHeader
        limit={limit}
      />
      <ViewRequest
        open={openView}
        requestId={selectedRequest?.id}
        onClose={handleCloseView}
      />
    </StyledRequestTable>
  );
};

export default RequestTable;
